import { useEffect, useRef, useState } from 'react'
import { useTranslate } from '@/utils/translate/translate-client'
import { DavidButton } from './DavidButton'

export function StoriesSongsSection() {
  const { t } = useTranslate('pif')
  const iframeRef = useRef<HTMLIFrameElement | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [playerReady, setPlayerReady] = useState(false)
  const baseVideoUrl =
    'https://www.youtube-nocookie.com/embed/O_HYdZ-Jq3Q?enablejsapi=1&playsinline=1&autoplay=0&mute=1&rel=0&modestbranding=1&controls=1'
  // Initialize with a server-safe value to avoid SSR hydration diffs
  const [videoSrc, setVideoSrc] = useState<string>(baseVideoUrl)

  useEffect(() => {
    try {
      const origin = window.location.origin
      setVideoSrc(`${baseVideoUrl}&origin=${encodeURIComponent(origin)}`)
    } catch {
      setVideoSrc(baseVideoUrl)
    }
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return
    const iframe = iframeRef.current
    const container = containerRef.current
    if (!iframe || !container) return

    const send = (func: string, args: unknown[] = []) => {
      iframe.contentWindow?.postMessage(JSON.stringify({ event: 'command', func, args }), '*')
    }

    const io = new IntersectionObserver(
      ([entry]) => {
        if (!playerReady) return
        if (entry?.isIntersecting && entry.intersectionRatio >= 0.5) {
          send('playVideo')
        } else {
          send('pauseVideo')
        }
      },
      { threshold: [0, 0.5] },
    )

    io.observe(container)

    const onVis = () => {
      if (document.hidden) send('pauseVideo')
    }
    document.addEventListener('visibilitychange', onVis)

    return () => {
      io.disconnect()
      document.removeEventListener('visibilitychange', onVis)
    }
  }, [playerReady])

  return (
    <section className="relative w-full bg-[#48c7d8] px-[16px] pb-[96px] pt-[72px] md:px-[64px] md:py-[160px]">
      <div className="relative mx-auto flex w-full max-w-[1440px] shrink-0 flex-col-reverse content-stretch items-start gap-[40px] md:gap-[96px]">
        {/* Content */}
        <div className="relative flex w-full max-w-[680px] shrink-0 flex-col content-stretch items-start gap-[40px]">
          {/* Copy */}
          <div className="relative flex w-full shrink-0 flex-col content-stretch items-start gap-[12px] not-italic text-[#141447]">
            <p className="font-angel-royal relative w-full shrink-0 text-[40px] lowercase not-italic leading-[40px] text-[#141447] md:text-[64px] md:leading-[64px]">
              {t('davidStoriesSongsTitle', 'Stories and Songs That Stay for a Lifetime')}
            </p>
            <p className="font-whitney-medium md:font-whitney-medium relative w-full shrink-0 text-[16px] not-italic leading-[24px] text-[#141447] md:text-[18px] md:leading-[28px]">
              {t(
                'davidStoriesSongsDescription',
                "Animated musicals shape generations. The songs and heroes kids grow up with help define who they become. When they see David's faith, courage, and heart — they'll carry those lessons for life.",
              )}
            </p>
          </div>

          <DavidButton variant="dark" openPifOverlay className="w-full md:w-auto">
            {t('payItForward', 'Pay it Forward')}
          </DavidButton>
        </div>

        {/* Video */}
        <div className="relative flex w-full shrink-0 flex-col content-stretch items-start gap-[1rem] md:items-end md:gap-[1.5rem]">
          <p className="font-whitney-medium relative hidden shrink-0 whitespace-pre text-nowrap text-right text-[12px] not-italic leading-[16px] text-[#141447] md:block">
            {t('davidFollowTheLightCaption', 'Watch the teaser trailer for the song "Follow the Light"')}
          </p>
          <div ref={containerRef} className="relative h-[300px] w-full overflow-hidden md:h-[700px] lg:rounded-xl">
            <iframe
              ref={iframeRef}
              src={videoSrc}
              title="Follow the Light - DAVID Teaser Trailer"
              className="h-full w-full"
              allow="autoplay; accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              loading="lazy"
              onLoad={() => setPlayerReady(true)}
            />
          </div>
          <p className="font-whitney-medium relative block shrink-0 whitespace-pre text-nowrap text-right text-[12px] not-italic leading-[16px] text-[#141447] md:hidden">
            {t('davidFollowTheLightCaption', 'Watch the teaser trailer for the song "Follow the Light"')}
          </p>
        </div>
      </div>
    </section>
  )
}
